import React from 'react';
import { Guitar, Users, Music, Sparkles, PartyPopper, Heart, Building2, Wheat, Beer, Landmark, HandHeart } from 'lucide-react';
import { services, venueTypes } from '../data/mock';

const iconMap = {
  guitar: Guitar,
  users: Users,
  music: Music
};

const venueIconMap = {
  'Weddings': Heart,
  'County Fairs': Wheat,
  'Private Parties': PartyPopper,
  'Bars & Pubs': Beer,
  'Honkytonks': Music,
  'Fundraisers': HandHeart,
  'Corporate Events': Building2,
  'Community Events': Landmark
};

const ServicesSection = () => {
  return (
    <section id="services" className="py-24 bg-stone-900 relative overflow-hidden">
      {/* Background Accents */}
      <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-r from-gold-500/5 to-transparent" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-gold-400/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-gold-500/10 border border-gold-500/20 rounded-full px-4 py-2 mb-6">
            <Sparkles className="w-4 h-4 text-gold-400" />
            <span className="text-gold-300 text-sm font-medium">Service Options</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Find Your Perfect <span className="text-gold-400">Sound</span>
          </h2>
          <p className="text-stone-400 text-lg max-w-2xl mx-auto">
            From intimate solo acoustic performances to full band shows, we have the perfect option for your event.
          </p>
        </div>

        {/* Service Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-20">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            const pricingId = service.id === 'solo' ? 'pricing-solo' : service.id === 'duo' ? 'pricing-duo' : 'pricing-fullband';
            return (
              <div
                key={service.id}
                className="group bg-stone-950/50 border border-stone-800 rounded-2xl p-8 hover:border-gold-500/50 transition-all duration-300 hover:shadow-xl hover:shadow-gold-500/10 hover:-translate-y-1 cursor-pointer"
                onClick={() => {
                  const element = document.getElementById(pricingId);
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    // Add a brief highlight effect
                    element.classList.add('ring-2', 'ring-gold-500');
                    setTimeout(() => {
                      element.classList.remove('ring-2', 'ring-gold-500');
                    }, 2000);
                  }
                }}
              >
                <div className="w-16 h-16 rounded-xl bg-gold-500/10 flex items-center justify-center mb-6 group-hover:bg-gold-500/20 transition-colors duration-300">
                  <IconComponent className="w-8 h-8 text-gold-400" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">{service.name}</h3>
                <p className="text-stone-400 leading-relaxed">{service.description}</p>
                
                {/* Decorative line */}
                <div className="mt-6 pt-6 border-t border-stone-800">
                  <span className="text-gold-400 text-sm font-medium group-hover:underline">View Pricing →</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Venue Types */}
        <div className="bg-stone-950/50 border border-stone-800 rounded-2xl p-8 sm:p-12">
          <h3 className="text-2xl font-bold text-white mb-8 text-center">Perfect For Any Venue</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {venueTypes.map((venue, index) => {
              const VenueIcon = venueIconMap[venue] || Music;
              return (
                <div
                  key={index}
                  className="flex items-center gap-3 bg-stone-900/50 border border-stone-800 rounded-xl p-4 hover:border-gold-500/30 hover:bg-stone-900 transition-all duration-200"
                >
                  <VenueIcon className="w-5 h-5 text-gold-400 flex-shrink-0" />
                  <span className="text-stone-300 text-sm font-medium">{venue}</span>
                </div>
              );
            })}         
          </div>
          <p className="text-center text-stone-500 mt-8 text-sm">
            Have a different venue in mind? Shoot us any dates you have in mind!
          </p>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
